import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Order, OrderWithGamas } from '../../../models/Order';
import { FormBuilder, FormGroup } from '@angular/forms';
import { OrderService } from '../../../services/entities/order.service';
import { DateService } from '../../../services/date.service';
import { Gama } from '../../../models/Gama';
import { PdfService } from '../../../services/pdf.service';
import { ApexNonAxisChartSeries, ApexChart, ApexResponsive, ApexTitleSubtitle, ApexTheme, ApexDataLabels, ApexTooltip } from 'ng-apexcharts';
import { cheese_chart_options } from '../../gamas/edit-gama/charts/cheese-chart';
import { Worker } from '../../../models/Worker';
import { GamaService } from '../../../services/entities/gama.service';

const ACTION_TYPES = ['view', 'edit', 'create']

export type ChartOptions = {
  display: boolean;
  series: ApexNonAxisChartSeries;
  chart: ApexChart;
  responsive: ApexResponsive[];
  labels: any;
  theme: ApexTheme;
  title: ApexTitleSubtitle;
  dataLabels: ApexDataLabels;
  tooltip: ApexTooltip;
};

@Component({
  selector: 'app-edit-order',
  templateUrl: './edit-order.component.html',
  styleUrl: './edit-order.component.scss'
})
export class EditOrderComponent implements OnInit {
  public cheese_chart_options: ChartOptions = cheese_chart_options

  constructor(private route: ActivatedRoute,
              private router: Router,
              private formBuilder: FormBuilder,
              private orderService: OrderService,
              private gamaService: GamaService,
              public dateService: DateService,
              private pdfService: PdfService){}

  //Action Types Vars
  action: string = 'edit'

  //Order Vars
  order: OrderWithGamas = {id: '', number: '', client: '', denomination: '', request: '', gamas: []}
  form_in_progress: boolean = false
  order_form!: FormGroup

  ngOnInit() {
    this.route.params.subscribe(async params => {
      this.action = params['action']

      if(!this.action || !ACTION_TYPES.includes(this.action)) this.router.navigate(['/orders'])
      
      switch(this.action){
        case 'create':
          this.initForm()
          break
        case 'view':
          this.form_in_progress = false
          this.order.id = params['order_id']

          if(!this.order.id) this.router.navigate(['/orders'])
          this.orderService.getOrderById(this.order.id).subscribe(
            order => {
              this.order = order
              this.setChartsValues(order.gamas)
            },
            error => {
              console.error('Order with ID: ' + this.order.id + ' not found.', error)
            }
          )
          break
        case 'edit':
          this.initForm()
          this.order.id = params['order_id']

          if(!this.order.id) this.router.navigate(['/orders'])
          this.orderService.getOrderById(this.order.id).subscribe(
            order => {
              this.order = order
              this.order_form.patchValue(this.order)
            },
            error => {
              console.error('Order with ID: ' + this.order.id + ' not found.', error)
            }
          )
          break
      }
    });
  }

  registerOrder(){
    this.orderService.createOrder(this.order_form.value).subscribe(
      (order: Order) => {
        this.router.navigate(['/orders/view/' + order.id])
      },
      (error) => {
        console.error('Error creating order:', error)
      }
    )
  }

  updateOrder(){
    const updated_order: Partial<Order> = {...this.order_form.value, id: this.order.id}
    this.orderService.updateOrder(updated_order).subscribe(
      (order: Order) => {
        this.router.navigate(['/orders/view/' + order.id])
      },
      (error) => {
        console.error('Error creating order:', error)
      }
    )
  }

  updateGamaStatus(gama: Gama){
    const gama_aux: Partial<Gama> = gama
    delete gama_aux.actions
    this.gamaService.updateGama({...gama_aux, active: !gama_aux.active}).subscribe(
      (updated_gama: Gama) => {
        const target_gama: Gama = this.order.gamas.find(g => g.id === updated_gama.id)!!
        target_gama.active = updated_gama.active
        console.log("updated_gama", updated_gama)
      },
      (error) => {
        console.error('Error updating gama status:', error)
      }
    )
  }

  initForm() {
    this.order_form = this.formBuilder.group(
      {
        number: [''],
        client: [''],
        denomination: [''],
        request: [''],
        mc: [false],
        start_date: [],
        deliver_date: [],
        delivery: [],
        new_deliver_date: [],
        review_date: [],
      }
    )
  }

  sortedGamas(){
    return this.order.gamas.sort((a, b) => a.index - b.index)
  }

  saveGamaPDF(gama: Gama){
    this.pdfService.saveGamaPDF(gama, this.order)
  }

  /*##################### Charts Actions #####################*/

  setChartsValues(gamas: Gama[]){
    const workers: Worker[] = [] // Array de trabajadores de la orden inicializado a vacío
    for(let gama of gamas) { // Itera sobre las gamas de la orden
      const action_workers: Worker[] = gama.actions.map(action => action.worker); // Obtiene los trabajadores de las acciones de la gama
      workers.push(...action_workers.filter(worker => !workers.find(w => w.id === worker.id))) // Añade los trabajadores de las acciones que no estén ya en el array de trabajadores
    }

    const user_work_time = this.calculateWorkTime(gamas); // Calcula el tiempo de trabajo de cada usuario
    this.cheese_chart_options.series = Object.values(user_work_time); // Establece los valores de la serie del gráfico de quesos
    this.cheese_chart_options.labels = Object.keys(user_work_time).map(worker_id => workers.find(worker => worker.id === worker_id)?.name ? `${workers.find(worker => worker.id === worker_id)?.name}: ${this.formatWorkedTime(user_work_time[worker_id])}` : 'Trabajador desconocido'); // Establece las etiquetas del gráfico de quesos
    this.cheese_chart_options.title.text = `Tiempo total ${this.formatWorkedTime(Object.values(user_work_time).reduce((acc, val) => acc + val, 0))}`; // Establece el título del gráfico de quesos
    this.cheese_chart_options.display = true; // Muestra el gráfico de quesos
  }

  calculateWorkTime(gamas: Gama[]): { [key: string]: number } {
    const user_work_time: { [key: string]: number } = {}; // Tiempo de trabajo por usuario
    const actions_by_user: { [key: string]: any[] } = {}; // Agrupamos las acciones por user_id

    for(let gama of gamas){ // Itera sobre las gamas para calcular el tiempo de trabajo de cada usuario
      gama.actions.forEach(action => { // Itera sobre las acciones de la gama
        if (!actions_by_user[action.worker.id]) { // Si no existe valor para la key del id del usuario en el objeto actions_by_user, lo crea
          actions_by_user[action.worker.id] = []; // Crea un array vacío para la key del id de dicho usuario
        }
        actions_by_user[action.worker.id].push(action); // Añade la acción al array del usuario
      });
    }
    
    // Calcular tiempo de trabajo para cada usuario
    for (const user_id in actions_by_user) { // Itera sobre las acciones agrupadas por usuario
      const actions = actions_by_user[user_id]; // Obtiene las acciones del usuario
      actions.sort((a, b) => new Date(a.creation_date).getTime() - new Date(b.creation_date).getTime()); // Ordena las acciones por fecha de creación
  
      let total_work_time = 0; // Tiempo total de trabajo del usuario inicializado a 0
      let start_time: Date | null = null;
  
      actions.forEach(action => { // Itera sobre las acciones del usuario
        if (action.type === 'start') { // Si la acción es de tipo 'start'
          start_time = new Date(action.creation_date); // Establece la fecha de inicio de la acción
        } else if ((action.type === 'end') && start_time) { // Si la acción es de tipo 'end' y hay una fecha de inicio previa
          const end_time = new Date(action.creation_date); // Establece la fecha de fin de la acción
          total_work_time += end_time.getTime() - start_time.getTime(); // Suma el tiempo de trabajo al tiempo total
          start_time = null; // Reinicia la fecha de inicio a null
        }
      });
  
      // Si hay un intervalo sin fecha de fin, usar la fecha actual
      if (start_time) { // Si hay una fecha de inicio sin fecha de fin (en de haber habido end, el start_time se hubiera reiniciado a null)
        const end_time = new Date(); // Establece la fecha de fin a la fecha actual
        total_work_time += end_time.getTime() - (start_time ? start_time : new Date()).getTime(); // Suma el tiempo de trabajo al tiempo total
      }
  
      user_work_time[user_id] = total_work_time; // Establece el tiempo total de trabajo del usuario en el objeto user_work_time
    }
  
    return (user_work_time) // Devuelve el objeto con el tiempo de trabajo por usuario
  }

  formatWorkedTime(val: number): string { // Formatea el tiempo trabajado
    const totalMinutes = Math.floor(val / 1000 / 60); // Convierte el tiempo en milisegundos a minutos
    const hours = Math.floor(totalMinutes / 60); // Calcula las horas
    const minutes = totalMinutes % 60; // Calcula los minutos
    return `${hours} horas ${minutes} minutos`; // Devuelve el tiempo trabajado en formato 'X horas Y minutos'
  }
}
